<template>
  <div
    class="min-h-100 bg-info"
    style="overflow-x:hidden;"
  >
    <div
      class="container bg-primary text-light"
    >
      <div
        v-for="(tab, index) in tabs"
        :id="tab.componentName || tab.title"
        :key="index"
        :ref="`section-${tab.componentName || tab.title}`"
        class="row section"
        :style="innerHeight"
      >
        <component
          :is="`section-${tab.componentName || tab.title}`"
          :show-fancy-scrollbar="showFancyScrollbar"
          @scrollTo="scrollToSection"
        />
      </div>
      <scroll-menu
        v-if="showFancyScrollbar"
        :selection="selection"
        :pages="tabs.map((t) => ({ caption: t.title, color: t['background-color'] }))"
        @scrollTo="scrollToSection"
      />
    </div>
  </div>
</template>

<script>
import SectionContact from '@/components/sections/SectionContact.vue';
import SectionSarah from '@/components/sections/SectionSarah.vue';
import SectionTitle from '@/components/sections/SectionTitle.vue';
import SectionTraining from '@/components/sections/SectionTraining.vue';
import SectionBeratung from '@/components/sections/SectionBeratung.vue';
import SectionHerangehensweise from '@/components/sections/SectionHerangehensweise.vue';
import SectionLeistungen from '@/components/sections/SectionLeistungen.vue';
import ScrollMenu from '@/components/sections/ScrollMenu.vue';

import tabs from '@/data/tabs';

export default {
  components: {
    SectionContact,
    SectionSarah,
    SectionTitle,
    SectionTraining,
    SectionBeratung,
    SectionHerangehensweise,
    SectionLeistungen,

    ScrollMenu,
  },
  data() {
    return {
      tabs,
      selection: 0,
      inMove: false,
      showFancyScrollbar: true,
      touchStartY: undefined,
      windowHeight: window.innerHeight,
    };
  },
  computed: {
    bgColor() {
      const originalColor = this.tabs[this.selection]['background-color'];

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(originalColor);
      return `background-color: rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)},0.6);`;
    },
    innerHeight() {
      return this.showFancyScrollbar ? `height:${this.windowHeight}px` : `min-height:${this.windowHeight}px`;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        if (!from || to.hash !== from.hash) {
          const hashIndex = tabs.findIndex((t) => `#${t.componentName}` === to.hash);
          if (hashIndex >= 0) {
            this.selection = hashIndex;
          }
        }
      },
    },
  },
  // we add event listeners for user scroll behaviour for different browsers
  created() {
    window.addEventListener('DOMMouseScroll', this.handleMouseWheelDOM); // Mozilla Firefox
    window.addEventListener('mousewheel', this.handleMouseWheel, {
      passive: false,
    }); // Other browsers
    window.addEventListener('touchstart', this.touchStart, {
      passive: false,
    }); // mobile devices
    window.addEventListener('touchmove', this.touchMove, {
      passive: false,
    });
    window.addEventListener('resize', this.onResize);
    this.$nextTick(() => {
      this.onResize();
    });
    window.addEventListener('keydown', this.onkey);
  },
  // we remove the event listeners when this component is removed
  destroyed() {
    window.removeEventListener('mousewheel', this.handleMouseWheel, {
      passive: false,
    }); // Other browsers
    window.removeEventListener('DOMMouseScroll', this.handleMouseWheelDOM); // Mozilla Firefox
    window.removeEventListener('touchstart', this.touchStart); // mobile devices
    window.removeEventListener('touchmove', this.touchMove); // mobile devices
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onkey);
    document.querySelector('body').style.overflow = 'auto';
  },
  methods: {
    onkey(e) {
      if (e.key === 'Home') {
        this.scrollToSection(0);
      } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        this.moveDown();
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        this.moveUp();
      } else if (e.key === 'End') {
        this.scrollToSection(tabs.length - 1);
      }
    },
    touchMove(e) {
      if (this.inMove) return false;
      if (!this.showFancyScrollbar) {
        return true;
      }
      if (this.touchStartY) {
        e.preventDefault();
        const currentY = e.touches[0].clientY;
        if (this.touchStartY < currentY) {
          this.moveUp();
        } else if (this.touchStartY > currentY) {
          this.moveDown();
        }
        this.touchStartY = undefined;
      } else {
        this.touchStartY = e.touches[0].clientY;
      }

      return false;
    },
    onResize() {
      this.windowHeight = window.innerHeight;

      const height = window.innerHeight;
      this.showFancyScrollbar = height > 800;
      if (this.showFancyScrollbar && this.selection > 0) {
        this.$nextTick(() => {
          this.$refs[`section-${tabs[this.selection].componentName || tabs[this.selection].title}`][0].scrollIntoView();
        });
      }
      if (this.showFancyScrollbar) {
        document.querySelector('body').style.overflow = 'hidden';
      } else {
        document.querySelector('body').style.overflow = 'auto';
      }
    },
    scrollToSection(id) {
      // we set a boolean here to avoid double scrolls. We unset it after .4 seconds
      if (!this.inMove) {
        this.inMove = true;
        setTimeout(() => { this.inMove = false; }, 400);

        // we update our selection variable
        this.selection = id;

        // we scroll to our new selected tab
        this.$refs[`section-${tabs[id].componentName || tabs[id].title}`][0].scrollIntoView({
          behavior: 'smooth',
        });

        // if its not the first tab we update the url #
        if (id > 0) {
          const newHash = `#${tabs[this.selection].componentName || tabs[this.selection].title}`;
          window.history.pushState({}, '', newHash);
        } else {
          window.history.pushState({}, '', '#');
        }
      }
    },
    handleMouseWheel(e) {
      // check if we scroll up or down
      if (!this.showFancyScrollbar) {
        return true;
      }
      if (e.wheelDelta < 30 && !this.inMove) {
        this.moveDown();
      } else if (e.wheelDelta > 30 && !this.inMove) {
        this.moveUp();
      }
      // prevent default cancels the default behaviour of scrolling
      // if (this.showFancyScrollbar) {
      e.preventDefault();
      return false;
      // }
      // return true;
    },
    moveUp() {
      // move up if we are not at pos 0
      if (this.selection > 0) {
        this.scrollToSection(this.selection - 1, true);
      }
    },
    moveDown() {
      // move down if we are not at the end
      if (this.selection < tabs.length - 1) {
        this.scrollToSection(this.selection + 1, true);
      }
    },
  },
};
</script>
